/**
 * Invitation Management Component - Admin interface for managing invitations
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Alert,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputLabel,
  Select,
  FormControl,
  CircularProgress,
} from '@mui/material';
import {
  Refresh,
  Cancel,
  Email,
  Add,
  MoreVert,
  CheckCircle,
  Schedule,
  Block,
  ContentCopy,
  Delete,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { apiService } from '@/services/api';
import { Invitation } from '@/types';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { formatDateDefault } from '@/utils/dateUtils';

const InvitationManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user: _currentUser } = useAuth();

  // State
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  
  // Create invitation dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [creating, setCreating] = useState(false);
  
  // Actions
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedInvitation, setSelectedInvitation] = useState<Invitation | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [invitationToCancel, setInvitationToCancel] = useState<string | null>(null);
  const [invitationToDelete, setInvitationToDelete] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Load invitations
  const loadInvitations = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.listInvitations(statusFilter || undefined);
      setInvitations(data);
    } catch (err: any) {
      console.error('Failed to load invitations:', err);
      setError(err.detail || t('admin.invitations.load_error', 'Failed to load invitations'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvitations();
  }, [statusFilter]);

  // Create invitation
  const handleCreateInvitation = async () => {
    if (!newEmail.trim()) {
      setError(t('admin.invitations.email_required', 'Email is required'));
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail.trim())) {
      setError(t('admin.invitations.invalid_email', 'Invalid email address'));
      return;
    }

    setCreating(true);
    setError(null);
    try {
      await apiService.createInvitation(newEmail.trim());
      setSuccess(t('admin.invitations.create_success', 'Invitation sent successfully'));
      setCreateDialogOpen(false);
      setNewEmail('');
      loadInvitations();
    } catch (err: any) {
      setError(err.detail || t('admin.invitations.create_error', 'Failed to create invitation'));
    } finally {
      setCreating(false);
    }
  };

  // Handle actions menu
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, invitation: Invitation) => {
    setAnchorEl(event.currentTarget);
    setSelectedInvitation(invitation);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedInvitation(null);
  };

  // Cancel invitation
  const handleCancelClick = () => {
    if (!selectedInvitation) return;
    setInvitationToCancel(selectedInvitation.id);
    setCancelDialogOpen(true);
    handleMenuClose();
  };

  const handleCancelConfirm = async () => {
    if (!invitationToCancel) return;
    
    setActionLoading(true);
    try {
      await apiService.cancelInvitation(invitationToCancel);
      setSuccess(t('admin.invitations.cancel_success', 'Invitation cancelled successfully'));
      setCancelDialogOpen(false);
      setInvitationToCancel(null);
      loadInvitations();
    } catch (err: any) {
      setError(err.detail || t('admin.invitations.cancel_error', 'Failed to cancel invitation'));
    } finally {
      setActionLoading(false);
    }
  };

  // Delete invitation permanently
  const handleDeleteClick = () => {
    if (!selectedInvitation) return;
    setInvitationToDelete(selectedInvitation.id);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!invitationToDelete) return;
    
    setActionLoading(true);
    try {
      await apiService.deleteInvitation(invitationToDelete);
      setSuccess(t('admin.invitations.deleted', 'Invitation deleted successfully'));
      setDeleteDialogOpen(false);
      setInvitationToDelete(null);
      loadInvitations();
    } catch (err: any) {
      setError(err.detail || t('admin.invitations.delete_error', 'Failed to delete invitation'));
    } finally {
      setActionLoading(false);
    }
  };

  // Resend invitation (create new one for same email)
  const handleResendInvitation = async () => {
    if (!selectedInvitation) return;
    
    setActionLoading(true);
    try {
      await apiService.resendInvitation(selectedInvitation.id);
      setSuccess(t('admin.invitations.resent', 'Invitation resent successfully'));
      handleMenuClose();
      loadInvitations();
    } catch (err: any) {
      setError(err.detail || t('admin.invitations.resend_error', 'Failed to resend invitation'));
    } finally {
      setActionLoading(false);
    }
  };

  // Copy token to clipboard
  const handleCopyToken = async (token: string) => {
    try {
      // Generate invitation link using current frontend URL
      const frontendUrl = window.location.origin;
      const invitationLink = `${frontendUrl}/accept-invitation?token=${token}`;
      
      await navigator.clipboard.writeText(invitationLink);
      setSuccess(t('admin.invitations.token_copied', 'Invitation link copied to clipboard!'));
    } catch (err) {
      // Fallback: copy just the token
      try {
        await navigator.clipboard.writeText(token);
        setSuccess(t('admin.invitations.token_copied_simple', 'Token copied to clipboard!'));
      } catch (err2) {
        setError(t('admin.invitations.copy_error', 'Failed to copy token'));
      }
    }
  };

  // Get status color
  const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
    switch (status) {
      case 'pending':
        return 'primary';
      case 'accepted':
        return 'success';
      case 'expired':
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Schedule />;
      case 'accepted':
        return <CheckCircle />;
      case 'expired':
      case 'cancelled':
        return <Block />;
      default:
        return null;
    }
  };

  // Clear messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">
          {t('admin.invitations.title', 'Invitation Management')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
            variant="contained"
          >
            {t('admin.invitations.create', 'Invite User')}
          </Button>
          <Button
            startIcon={<Refresh />}
            onClick={loadInvitations}
            disabled={loading}
            variant="outlined"
          >
            {t('admin.invitations.refresh', 'Refresh')}
          </Button>
        </Box>
      </Box>

      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{t('admin.invitations.status', 'Status')}</InputLabel>
          <Select
            value={statusFilter}
            label={t('admin.invitations.status', 'Status')}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">{t('admin.invitations.all_statuses', 'All')}</MenuItem>
            <MenuItem value="pending">{t('admin.invitations.pending', 'Pending')}</MenuItem>
            <MenuItem value="accepted">{t('admin.invitations.accepted', 'Accepted')}</MenuItem>
            <MenuItem value="expired">{t('admin.invitations.expired', 'Expired')}</MenuItem>
            <MenuItem value="cancelled">{t('admin.invitations.cancelled', 'Cancelled')}</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {/* Invitations Table */}
      <TableContainer component={Paper}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('admin.invitations.email', 'Email')}</TableCell>
                <TableCell>{t('admin.invitations.invited_by', 'Invited By')}</TableCell>
                <TableCell>{t('admin.invitations.status', 'Status')}</TableCell>
                <TableCell>{t('admin.invitations.token', 'Token')}</TableCell>
                <TableCell>{t('admin.invitations.created', 'Created')}</TableCell>
                <TableCell>{t('admin.invitations.expires', 'Expires')}</TableCell>
                <TableCell>{t('admin.invitations.accepted', 'Accepted')}</TableCell>
                <TableCell align="right">{t('admin.invitations.actions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invitations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {t('admin.invitations.no_invitations', 'No invitations found')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                invitations.map((invitation) => (
                  <TableRow key={invitation.id} hover>
                    <TableCell>{invitation.email}</TableCell>
                    <TableCell>{invitation.inviter_name || '-'}</TableCell>
                    <TableCell>
                      {(() => {
                        const statusIcon = getStatusIcon(invitation.status);
                        return (
                          <Chip
                            {...(statusIcon ? { icon: statusIcon } : {})}
                            label={t(`admin.invitations.${invitation.status}`, invitation.status)}
                            color={getStatusColor(invitation.status)}
                            size="small"
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {invitation.token ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.75rem',
                              maxWidth: '200px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={invitation.token}
                          >
                            {invitation.token.substring(0, 20)}...
                          </Typography>
                          <Tooltip title={t('admin.invitations.copy_token', 'Copy invitation link')}>
                            <IconButton
                              size="small"
                              onClick={() => handleCopyToken(invitation.token!)}
                              sx={{ ml: 0.5 }}
                            >
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {invitation.created_at ? formatDateDefault(invitation.created_at) : '-'}
                    </TableCell>
                    <TableCell>
                      {invitation.expires_at ? formatDateDefault(invitation.expires_at) : '-'}
                    </TableCell>
                    <TableCell>
                      {invitation.accepted_at ? formatDateDefault(invitation.accepted_at) : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('admin.invitations.actions', 'Actions')}>
                        <IconButton
                          onClick={(e) => handleMenuOpen(e, invitation)}
                          size="small"
                        >
                          <MoreVert />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Create Invitation Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('admin.invitations.create_title', 'Invite New User')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('admin.invitations.email', 'Email Address')}
            type="email"
            fullWidth
            variant="outlined"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="user@example.com"
            helperText={t('admin.invitations.create_helper', 'An invitation email will be sent to this address')}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>
            {t('admin.invitations.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleCreateInvitation}
            variant="contained"
            disabled={creating || !newEmail.trim()}
            startIcon={creating ? <CircularProgress size={16} /> : <Email />}
          >
            {creating
              ? t('admin.invitations.sending', 'Sending...')
              : t('admin.invitations.send', 'Send Invitation')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedInvitation?.status === 'pending' && (
          <>
            {selectedInvitation.token && (
              <MenuItem
                onClick={() => {
                  handleCopyToken(selectedInvitation!.token!);
                  handleMenuClose();
                }}
              >
                <ContentCopy sx={{ mr: 1 }} />
                {t('admin.invitations.copy_token', 'Copy Invitation Link')}
              </MenuItem>
            )}
            <MenuItem onClick={handleResendInvitation} disabled={actionLoading}>
              <Email sx={{ mr: 1 }} />
              {t('admin.invitations.resend', 'Resend Invitation')}
            </MenuItem>
            <MenuItem
              onClick={handleCancelClick}
              disabled={actionLoading}
              sx={{ color: 'warning.main' }}
            >
              <Cancel sx={{ mr: 1 }} />
              {t('admin.invitations.cancel', 'Cancel Invitation')}
            </MenuItem>
          </>
        )}
        <MenuItem
          onClick={handleDeleteClick}
          disabled={actionLoading}
          sx={{ color: 'error.main' }}
        >
          <Delete sx={{ mr: 1 }} />
          {t('admin.invitations.delete', 'Delete Invitation')}
        </MenuItem>
      </Menu>

      {/* Cancel Confirmation Dialog */}
      <ConfirmDialog
        open={cancelDialogOpen}
        onClose={() => {
          setCancelDialogOpen(false);
          setInvitationToCancel(null);
        }}
        onConfirm={handleCancelConfirm}
        title={t('admin.invitations.cancel_confirm_title', 'Cancel Invitation')}
        message={t(
          'admin.invitations.cancel_confirm_message',
          'Are you sure you want to cancel this invitation? The user will not be able to use this invitation link.'
        )}
        confirmText={t('admin.invitations.cancel', 'Cancel Invitation')}
        severity="warning"
        isLoading={actionLoading}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setInvitationToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={t('admin.invitations.delete_confirm_title', 'Delete Invitation')}
        message={t(
          'admin.invitations.delete_confirm_message',
          'Are you sure you want to permanently delete this invitation? This action cannot be undone and the invitation will be removed from the system.'
        )}
        confirmText={t('admin.invitations.delete', 'Delete')}
        severity="error"
        isLoading={actionLoading}
      />
    </Box>
  );
};

export default InvitationManagement;
